// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// "I came here to build" (M13, ADR 0015 — "the headset lays out").
//
// The Builder's headset door (`PublishPanel`, on a browser that can present) cannot start the
// session itself: a navigation spends the user activation a session needs, so the author lands on
// the front door and presses Enter MR there. Without a word carried across that hop, what they
// get is the *game* — the draft, playing, with a wrist menu that has a Build row somewhere on it —
// and the first author to walk this path said exactly that: *"it goes to playing the machine; it
// does not start in the builder."* This is the word.
//
// **`sessionStorage`, not `localStorage`.** An intent is scoped to the tab that formed it and to
// now: it must survive the navigation and a reload, and it must not still be there a week later
// when the same browser opens the front door to play. The one store with that lifetime is the
// session's own. Read-and-clear, so a session that honours it honours it once; the front door
// peeks without clearing so its buttons can say what they are about to do.
//
// Never throws, for `storage/local.ts`'s reason: a preference that cannot be saved is a small
// annoyance, and a page that will not render because it could not save one is not.

import type { StorageLike } from '../../storage/local'

/** Where the intent lives, for as long as the tab does. */
export const BUILD_INTENT_KEY = 'hcc.pinball.want-build'

/** The browser's `sessionStorage`, if it is there and works. Same proof `safeStorage` makes. */
export function safeSession(probeKey = 'hcc.pinball.probe'): StorageLike | undefined {
  try {
    const store = (globalThis as { sessionStorage?: StorageLike }).sessionStorage
    if (store === undefined || store === null) return undefined
    store.setItem(probeKey, '1')
    store.removeItem(probeKey)
    return store
  } catch {
    return undefined
  }
}

/** The Builder's door, on the way out: the next session on this tab should start in Build mode. */
export function askForBuild(storage: StorageLike | undefined = safeSession()): boolean {
  try {
    storage?.setItem(BUILD_INTENT_KEY, '1')
    return storage !== undefined
  } catch {
    return false
  }
}

/** Is a build wanted? A peek: the front door's buttons read this to say what they will do. */
export function buildWanted(storage: StorageLike | undefined = safeSession()): boolean {
  try {
    return storage?.getItem(BUILD_INTENT_KEY) === '1'
  } catch {
    return false
  }
}

/**
 * Take the intent: true once, then false until asked for again.
 *
 * Read by the session as it starts, so a recycle (XR-030) — which ends and restarts the session
 * with Build mode still on — does not read it a second time, and a later session on the same tab
 * that was not asked for starts as a game.
 */
export function takeBuildIntent(storage: StorageLike | undefined = safeSession()): boolean {
  const wanted = buildWanted(storage)
  if (wanted) {
    try {
      storage?.removeItem(BUILD_INTENT_KEY)
    } catch {
      // Taken as far as this tab is concerned; the next read will say so again, and that is the
      // small annoyance rather than the page that will not render.
    }
  }
  return wanted
}
